import { BlogCard } from './types';

export const nhsVsPrivateTrtBlogCards: BlogCard[] = [
  {
    title: {
      content:
        '### **GP Pathway vs Private TRT in the UK:** \n\n ## How to Choose the Right Route',
      styles: {
        h2: 'font-mulish font-bold text-royal-875 mb-0 text-base md:text-lg leading-tight',
        h3: 'font-mulish font-bold text-royal-875 text-base md:text-lg leading-tight line-clamp-2',
        strong:
          'font-mulish font-bold text-royal-875 text-base md:text-lg leading-tight',
        p: 'mb-0 mt-3',
      },
      topicsStyles: {
        h2: 'font-mulish text-theme-primary text-3xl md:text-4xl mb-4 md:mb-6 font-black leading-tight line-clamp-2',
        h3: 'font-mulish text-theme-primary text-3xl md:text-4xl mb-4 md:mb-6 pt-2 font-black',
        strong: 'font-mulish text-text-subtitle font-bold',
        p: 'font-mulish mb-0 text-2xl md:text-3xl p-2',
      },
    },
    description: {
      content:
        'NHS TRT takes 6 to 12 months. Private starts in 2 to 4 weeks. Compare both on cost, speed and eligibility. Book at LIPS Healthcare, Battersea Power Station.',
      styles: {
        p: 'text-sm md:text-base text-text-default font-medium leading-relaxed mb-3 line-clamp-3 md:line-clamp-4',
      },
    },
    metaTitle: 'NHS vs Private TRT UK: Cost, Speed and Access Compared',
    imageUrl: 'testosterone-replacement-therapy.webp',
    bannerUrl: 'testosterone-replacement-therapy.webp',
    bannerDescription:
      'Getting TRT through the standard GP route can take 6 to 12 months from first appointment to treatment, and many patients are declined on eligibility grounds. Private TRT clinics offer a faster route: most patients begin treatment within 2 to 4 weeks of their first consultation. This guide compares both pathways on cost, speed, access, and quality of care so you can make the right decision.',
    slug: 'nhs-vs-private-trt-uk',
    contentType: 'article',
    speciality: 'TRT',
    date: {
      content: '30, September 2026',
      iconUrl: '/images/blogs/calendar.svg',
      styles: {
        p: 'text-gray-500 text-xs',
      },
    },
    author: {
      content: 'LIPS Healthcare Marketing Team',
      styles: {
        p: 'text-indigo-650 font-bold text-xs',
        h3: 'text-theme-primary font-black text-2xl md:text-4xl',
      },
    },
    tableOfContents: {
      heading: {
        content: '### TABLE OF CONTENTS:',
        styles: {
          h3: 'font-Mulish font-bold text-royal-875 mb-3 text-lg md:text-xl',
        },
      },
      content: [
        {
          text: 'Getting TRT through your GP: eligibility, waiting times, and reality',
          id: 'getting-trt-through-your-gp',
        },
        {
          text: 'Private TRT in the UK: what the process looks like from day one',
          id: 'private-trt-process-from-day-one',
        },
        {
          text: 'GP pathway vs private TRT: side-by-side comparison',
          id: 'gp-pathway-vs-private-trt-comparison',
        },
        {
          text: 'Speed of access: the biggest difference between both routes',
          id: 'speed-of-access-difference',
        },
        {
          text: 'How to choose the right route for private TRT in the UK',
          id: 'how-to-choose-the-right-route',
        },
        {
          text: 'Frequently asked questions about NHS vs private TRT',
          id: 'frequently-asked-questions',
        },
      ],
      styles: {
        p: 'text-lg text-teal-500 underline leading-snug text-left cursor-pointer font-semibold',
      },
    },
    content: {
      content: `
<span id="getting-trt-through-your-gp"></span>

### Getting TRT through your GP: eligibility, waiting times, and reality

The standard pathway for TRT begins with your GP. If your testosterone levels are confirmed low and your GP agrees that treatment is warranted, they will refer you to an endocrinologist or urologist for specialist assessment. That referral is not guaranteed.

<div align="center">\n\n
![Getting TRT through your GP pathway](trt-pills.webp "600x400")\n\n
</div>

Waiting times from GP referral to first specialist appointment can exceed 6 to 12 months in many specialist services across the UK. Standard eligibility criteria are also tightly defined. A total testosterone below 8 nmol/L is generally required for automatic treatment consideration. Men with levels between 8 and 12 nmol/L who have significant symptoms face real barriers to access, even where clinical guidelines suggest a case for therapy. 

State-funded TRT limits your choice of medication to specific approved preparations. Monitoring frequency and format varies by service, and there is no guarantee of continuity of care with the same clinician.

<span id="private-trt-process-from-day-one"></span>

### Private TRT in the UK: what the process looks like from day one

Private TRT removes the gatekeeping. You book directly with a specialist, with no GP referral required. A blood test confirms your hormone levels, and if your results and symptoms support a clinical case for treatment, you can begin within two to four weeks. For patients in London looking for private TRT in the UK, LIPS Healthcare at Battersea Power Station is a consultant-led option with no waiting list.

<div align="center">\n\n
![Private TRT consultation at LIPS Healthcare](trt-consultant-patient.webp "600x400")\n\n
</div>

At LIPS Healthcare in Turbine Hall B, Battersea Power Station, your first consultation is led by a specialist physician in men's health and hormone optimisation. Blood tests are processed on-site with results back within 24 to 48 hours. Patients from Chelsea, Clapham, Vauxhall, and Pimlico can book directly via the Northern line or London Overground. 

Private TRT in the UK also gives you access to a broader range of treatment options: injections or gel, with a monitoring protocol tailored to your clinical picture rather than a standard approved formulary.

<div style="text-align:center;margin:0.5rem 0;"><strong><a href="/trt#booking-calendar-widget" target="_blank" rel="noopener noreferrer">Book a private TRT consultation at LIPS Healthcare</a></strong></div>

<span id="gp-pathway-vs-private-trt-comparison"></span>

### GP pathway vs private TRT: side-by-side comparison

The two routes differ significantly on four dimensions: access, speed, cost, and medication choice. The table below sets these out.

<div style="display:grid;grid-template-columns:minmax(110px,1fr) minmax(150px,1.15fr) minmax(150px,1.15fr);gap:0;width:100%;margin:1.5rem 0;">
<div style="padding:16px 12px 16px 4px;"></div>
<div style="background:#ECF3F2;border-radius:16px 16px 0 0;padding:18px 14px;font-size:16px;font-weight:800;color:#1E2A8A;line-height:1.3;">Private (LIPS Healthcare)</div>
<div style="padding:18px 12px;font-size:16px;font-weight:800;color:#1E2A8A;line-height:1.3;">GP Pathway</div>
<div style="padding:16px 12px 16px 4px;display:flex;align-items:center;font-size:14px;font-weight:700;color:#1E2A8A;border-bottom:1px solid #E5E7EB;">Referral needed?</div><div style="background:#ECF3F2;padding:16px 14px;display:flex;align-items:center;gap:10px;font-size:14px;font-weight:600;color:#1E2A8A;border-bottom:1px solid #D7E5E2;line-height:1.4;"><span style="width:20px;height:20px;border-radius:9999px;background:#14B8A6;color:#fff;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;font-size:11px;font-weight:700;line-height:1;">✓</span><span>No - book direct</span></div><div style="padding:16px 12px;display:flex;align-items:center;gap:10px;font-size:14px;font-weight:600;color:#1E2A8A;border-bottom:1px solid #E5E7EB;line-height:1.4;"><span style="width:20px;height:20px;border-radius:9999px;background:#F43F5E;color:#fff;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;font-size:10px;font-weight:700;line-height:1;">✕</span><span>Yes - GP referral required</span></div>
<div style="padding:16px 12px 16px 4px;display:flex;align-items:center;font-size:14px;font-weight:700;color:#1E2A8A;border-bottom:1px solid #E5E7EB;">Time to treatment</div><div style="background:#ECF3F2;padding:16px 14px;display:flex;align-items:center;gap:10px;font-size:14px;font-weight:600;color:#1E2A8A;border-bottom:1px solid #D7E5E2;line-height:1.4;"><span style="width:20px;height:20px;border-radius:9999px;background:#14B8A6;color:#fff;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;font-size:11px;font-weight:700;line-height:1;">✓</span><span>2 to 4 weeks</span></div><div style="padding:16px 12px;display:flex;align-items:center;gap:10px;font-size:14px;font-weight:600;color:#1E2A8A;border-bottom:1px solid #E5E7EB;line-height:1.4;"><span style="width:20px;height:20px;border-radius:9999px;background:#F43F5E;color:#fff;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;font-size:10px;font-weight:700;line-height:1;">✕</span><span>6 to 12+ months</span></div>
<div style="padding:16px 12px 16px 4px;display:flex;align-items:center;font-size:14px;font-weight:700;color:#1E2A8A;border-bottom:1px solid #E5E7EB;">Eligibility threshold</div><div style="background:#ECF3F2;padding:16px 14px;display:flex;align-items:center;gap:10px;font-size:14px;font-weight:600;color:#1E2A8A;border-bottom:1px solid #D7E5E2;line-height:1.4;"><span style="width:20px;height:20px;border-radius:9999px;background:#14B8A6;color:#fff;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;font-size:11px;font-weight:700;line-height:1;">✓</span><span>Assessed case by case</span></div><div style="padding:16px 12px;display:flex;align-items:center;gap:10px;font-size:14px;font-weight:600;color:#1E2A8A;border-bottom:1px solid #E5E7EB;line-height:1.4;"><span style="width:20px;height:20px;border-radius:9999px;background:#F43F5E;color:#fff;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;font-size:10px;font-weight:700;line-height:1;">✕</span><span>Typically below 8 nmol/L</span></div>
<div style="padding:16px 12px 16px 4px;display:flex;align-items:center;font-size:14px;font-weight:700;color:#1E2A8A;border-bottom:1px solid #E5E7EB;">Medication choice</div><div style="background:#ECF3F2;padding:16px 14px;display:flex;align-items:center;gap:10px;font-size:14px;font-weight:600;color:#1E2A8A;border-bottom:1px solid #D7E5E2;line-height:1.4;"><span style="width:20px;height:20px;border-radius:9999px;background:#14B8A6;color:#fff;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;font-size:11px;font-weight:700;line-height:1;">✓</span><span>Injections, gel</span></div><div style="padding:16px 12px;display:flex;align-items:center;gap:10px;font-size:14px;font-weight:600;color:#1E2A8A;border-bottom:1px solid #E5E7EB;line-height:1.4;"><span style="width:20px;height:20px;border-radius:9999px;background:#F43F5E;color:#fff;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;font-size:10px;font-weight:700;line-height:1;">✕</span><span>Limited approved formulary</span></div>
<div style="padding:16px 12px 16px 4px;display:flex;align-items:center;font-size:14px;font-weight:700;color:#1E2A8A;border-bottom:1px solid #E5E7EB;">Monitoring</div><div style="background:#ECF3F2;padding:16px 14px;display:flex;align-items:center;gap:10px;font-size:14px;font-weight:600;color:#1E2A8A;border-bottom:1px solid #D7E5E2;line-height:1.4;"><span style="width:20px;height:20px;border-radius:9999px;background:#14B8A6;color:#fff;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;font-size:11px;font-weight:700;line-height:1;">✓</span><span>Consistent, on-site protocol</span></div><div style="padding:16px 12px;display:flex;align-items:center;gap:10px;font-size:14px;font-weight:600;color:#1E2A8A;border-bottom:1px solid #E5E7EB;line-height:1.4;"><span style="width:20px;height:20px;border-radius:9999px;background:#F43F5E;color:#fff;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;font-size:10px;font-weight:700;line-height:1;">✕</span><span>Varies by service</span></div>
<div style="padding:16px 12px 16px 4px;display:flex;align-items:center;font-size:14px;font-weight:700;color:#1E2A8A;border-bottom:1px solid #E5E7EB;">Cost</div><div style="background:#ECF3F2;padding:16px 14px;display:flex;align-items:center;gap:10px;font-size:14px;font-weight:600;color:#1E2A8A;border-bottom:1px solid #D7E5E2;line-height:1.4;"><span style="width:20px;height:20px;border-radius:9999px;background:#14B8A6;color:#fff;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;font-size:11px;font-weight:700;line-height:1;">✓</span><span>£150 to £400 per month</span></div><div style="padding:16px 12px;display:flex;align-items:center;gap:10px;font-size:14px;font-weight:600;color:#1E2A8A;border-bottom:1px solid #E5E7EB;line-height:1.4;"><span style="width:20px;height:20px;border-radius:9999px;background:#F43F5E;color:#fff;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;font-size:10px;font-weight:700;line-height:1;">✕</span><span>Free (if eligible)</span></div>
<div style="padding:16px 12px 16px 4px;display:flex;align-items:center;font-size:14px;font-weight:700;color:#1E2A8A;border-bottom:none;">Continuity of care</div><div style="background:#ECF3F2;border-radius:0 0 16px 16px;padding:16px 14px;display:flex;align-items:center;gap:10px;font-size:14px;font-weight:600;color:#1E2A8A;border-bottom:none;line-height:1.4;"><span style="width:20px;height:20px;border-radius:9999px;background:#14B8A6;color:#fff;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;font-size:11px;font-weight:700;line-height:1;">✓</span><span>Same specialist throughout</span></div><div style="padding:16px 12px;display:flex;align-items:center;gap:10px;font-size:14px;font-weight:600;color:#1E2A8A;border-bottom:none;line-height:1.4;"><span style="width:20px;height:20px;border-radius:9999px;background:#F43F5E;color:#fff;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;font-size:10px;font-weight:700;line-height:1;">✕</span><span>Varies by service</span></div>
</div>

<span id="speed-of-access-difference"></span>

### Speed of access: the biggest difference between both routes

Speed is where the two routes differ most. Most men pursuing TRT through the standard GP route wait between 6 and 12 months before treatment begins. Some wait considerably longer.

<div align="center">\n\n
![Comparing NHS and private TRT options](doctor-patient-low-t.webp "600x400")\n\n
</div>

Low testosterone does not pause while you wait for a referral. Symptoms affecting energy, mood, libido, sleep, and cognitive function continue throughout that period. For men whose daily life, work performance, or relationships are significantly affected, the timeline matters.

At LIPS Healthcare in Battersea and Nine Elms, most patients start treatment within two to four weeks of their first appointment. On-site blood tests, fast result turnaround, and direct access to a specialist are what make that possible.

<span id="how-to-choose-the-right-route"></span>

### How to choose the right route for private TRT in the UK

#### Choose the GP pathway if:

* Your testosterone is clearly below clinical thresholds, your GP is supportive, you have no urgent symptoms, and you are able to wait for the referral process to run its course. The treatment is free and clinically appropriate.

#### Consider private TRT in the UK if:

* Your levels sit in the borderline range and your GP has declined to refer, you have been waiting months without progress, your symptoms are significantly affecting your quality of life, or you want a trt blood test uk result reviewed by a specialist within days rather than months.

A private assessment does not commit you to treatment. At LIPS Healthcare, your consultation will give you a clear clinical picture and a straightforward recommendation based on your blood results and symptoms.

><span style="font-weight: bold; color: #2A347A;">Fertility consideration:</span>
>
>TRT suppresses the body's natural testosterone production and reduces sperm output. For men who may wish to have children in the future, this must be discussed with your clinician before treatment begins. Regardless of which route you take, fertility preservation options should be raised at the initial assessment.

<div align="center">\n\n
![Comparing NHS and private TRT options](preparing-for-trt-replacement.webp "600x400")\n\n
</div>

TRT is not appropriate for men with active prostate or breast cancer, a baseline haematocrit above 0.54, severe untreated obstructive sleep apnoea, or uncontrolled heart failure. These conditions must be evaluated before treatment is considered on either pathway.

TRT is also a long-term commitment on both routes. Once started, it suppresses the body's natural testosterone production, and stopping requires careful clinical management. This should form part of any informed consent discussion before treatment begins.

<div style="text-align:center;margin:0.5rem 0;"><strong><a href="/trt#booking-calendar-widget" target="_blank" rel="noopener noreferrer">Start your TRT assessment at LIPS Healthcare</a></strong></div>

><span style="font-weight: bold; color: #2A347A;">Medical disclaimer</span>
>
>This article is for informational purposes only. It does not constitute medical advice, a diagnosis, or a treatment recommendation. Always consult a qualified medical professional before starting or changing any treatment. Waiting time figures referenced in this article are approximate and vary by service. Private pricing ranges are illustrative and based on typical market rates. Contact LIPS Healthcare directly for current fees.

<span id="frequently-asked-questions"></span>
`,
      styles: {
        h3: 'text-lg md:text-[32px] font-bold text-theme-primary mb-4',
        h4: 'text-lg md:text-[20px] font-bold text-theme-primary mb-4',
        p: 'text-sm md:text-base text-text-default font-medium leading-relaxed mb-3',
        a: 'font-semibold text-text-action',
        em: 'not-italic font-bold',
        strong:
          'inline-block bg-theme-primary hover:bg-white border border-theme-primary text-white px-6 py-3 rounded-lg font-bold no-underline cursor-pointer my-4 text-center [&_a]:text-white [&_a]:no-underline [&_a]:font-bold hover:[&_a]:text-theme-primary',
        table:
          'w-full table-fixed border-collapse text-xs md:text-sm border-2 border-black',
        th: 'border-2 border-black bg-text-muted-blue px-2 py-2 md:px-4 md:py-3 text-left font-semibold text-theme-primary whitespace-normal wrap-break-word',
        td: 'border-2 border-black px-2 py-2 md:px-4 md:py-3 align-top font-semibold whitespace-normal wrap-break-word',
        ul: 'list-disc pl-5 mb-4 space-y-2',
        li: 'text-sm md:text-base text-text-default font-medium leading-relaxed',
        blockquote:
          'border-l-4 border-theme-primary bg-[#E0E7FF] rounded-lg p-4 my-8 text-sm text-text-default not-italic',
      },
      tableOptions: {
        width: '100%',
        columnWidths: ['24%', '38%', '38%'],
      },
    },
    faq: {
      heading: {
        content: '#### Frequently asked questions about NHS vs private TRT',
        styles: {
          h4: 'text-xl md:text-2xl font-bold text-theme-primary mb-6',
        },
      },
      items: [
        {
          question: {
            content: 'Can I get TRT through my GP?',
          },
          answer: {
            content:
              'Yes, if you meet the standard eligibility criteria. A confirmed diagnosis of hypogonadism, typically with total testosterone below 8 nmol/L, is generally required. Your GP must agree to refer you to a specialist, and waiting times for that referral can exceed 6 to 12 months in many services.',
          },
        },
        {
          question: {
            content:
              'Is private TRT worth it compared to going through your GP?',
          },
          answer: {
            content:
              'It depends on your situation. If you qualify through the standard pathway and can wait, that route is free and appropriate. For men who cannot get a referral, sit in the borderline range, or cannot wait due to the daily impact of symptoms, private TRT offers faster access, a broader range of treatment options, and consistent specialist monitoring.',
          },
        },
        {
          question: {
            content: 'How long does it take to get TRT through the GP route?',
          },
          answer: {
            content:
              'Between 6 and 12 months in most cases, from initial GP appointment to starting treatment. This includes time for blood tests, a specialist referral, and a specialist assessment before any treatment decision is made. Some men wait longer.',
          },
        },
        {
          question: {
            content: 'How quickly can I start private TRT?',
          },
          answer: {
            content:
              'Most patients at LIPS Healthcare begin treatment within 2 to 4 weeks of their first consultation, subject to blood test results confirming clinical eligibility.',
          },
        },
        {
          question: {
            content: 'Do I need a GP referral for private TRT?',
          },
          answer: {
            content:
              'No. LIPS Healthcare accepts self-referred patients directly. No GP referral is required to book a private TRT consultation or to begin treatment.',
          },
        },
        {
          question: {
            content: 'Where can I get private TRT in London?',
          },
          answer: {
            content:
              'LIPS Healthcare offers private TRT in London at Battersea Power Station, accessible via the Northern Line and London Overground. Patients from Chelsea, Clapham, Vauxhall, Nine Elms, and Wandsworth are seen within a week of booking, with no waiting list and no GP referral required.',
          },
        },
      ],
    },
  },
];
